import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback
} from 'reactstrap'
import { Spinner } from 'react-bootstrap'
import { Toast } from 'primereact/toast'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import { API_URL } from '../utils/Constants/URLS'

/**
 * CreateEwayBillModal Component
 *
 * A production-grade modal component for generating E-way Bills for invoices.
 * Handles all required fields with proper validation and error handling.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.toggle - Function to toggle modal
 * @param {Object} props.invoice - Invoice object containing all invoice details
 * @param {Function} props.onSuccess - Callback function on successful E-way Bill generation
 */
const CreateEwayBillModal = ({ isOpen, toggle, invoice, onSuccess }) => {
  const toast = useRef(null)
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  // Form state management
  const [formData, setFormData] = useState({
    transMode: '1', // Default: Road

    vehicleNo: '',
    vehicleType: 'R' // Default: Regular
  })

  // Validation errors state
  const [errors, setErrors] = useState({})

  // Loading and error states
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(null)

  // Transportation mode options
  const transportModeOptions = [
    { value: '1', label: 'Road' },
    { value: '2', label: 'Rail' },
    { value: '3', label: 'Air' },
    { value: '4', label: 'Ship' }
  ]

  // Vehicle type options
  const vehicleTypeOptions = [
    { value: 'R', label: 'Regular' },
    { value: 'O', label: 'Over Dimensional Cargo (ODC)' }
  ]

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setFormData({
      transMode: '1',
      transDistance: '0',
      vehicleNo: '',
      vehicleType: 'R',
      transporterId: '',
      transporterName: ''
    })
    setErrors({})
    setApiError(null)
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!loading) {
      resetForm()
      toggle()
    }
  }

  /**
   * Handle input change with validation
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }

    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null)
    }
  }

  /**
   * Validate form fields
   * @returns {boolean} - Returns true if form is valid
   */
  const validateForm = () => {
    const newErrors = {}

    // Validate transportation mode
    if (!formData.transMode) {
      newErrors.transMode = 'Transportation mode is required'
    }

    // Validate vehicle number (required for road transport)
    if (formData.transMode === '1') {
      if (!formData.vehicleNo || formData.vehicleNo.trim() === '') {
        newErrors.vehicleNo = 'Vehicle number is required for road transport'
      } else {
        // Basic vehicle number format validation (Indian format)
        const vehicleRegex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{4}$/
        if (!vehicleRegex.test(formData.vehicleNo.replace(/\s+/g, ''))) {
          newErrors.vehicleNo =
            'Invalid vehicle number format (e.g., KA01AB1234)'
        }
      }

      // Validate vehicle type for road transport
      if (!formData.vehicleType) {
        newErrors.vehicleType = 'Vehicle type is required for road transport'
      }
    }

    // Validate transporter details (optional but if one is filled, both should be)
    if (formData.transporterId && !formData.transporterName) {
      newErrors.transporterName =
        'Transporter name is required when ID is provided'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Validate form
    if (!validateForm()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fix all validation errors before submitting',
        life: 3000
      })
      return
    }

    // Check if invoice exists
    if (!invoice || !invoice._id) {
      setApiError('Invalid invoice data')
      return
    }

    try {
      setLoading(true)
      setApiError(null)

      // Prepare request payload
      const payload = {
        invoiceId: invoice._id,
        transMode: formData.transMode,
        transDistance: formData.transDistance,
        vehicleNo: formData.vehicleNo.replace(/\s+/g, '').toUpperCase(),
        vehicleType: formData.vehicleType,
        transporterId: formData.transporterId.trim(),
        transporterName: formData.transporterName.trim()
      }

      // Make API call
      const response = await fetch(`${API_URL}ewaybills/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload),
        redirect: 'follow'
      })

      // Handle response
      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = 'Failed to generate E-way Bill'

        try {
          const errorJson = JSON.parse(errorText)
          errorMessage = errorJson.message || errorJson.error || errorMessage
        } catch {
          errorMessage = errorText || errorMessage
        }

        throw new Error(errorMessage)
      }

      const result = await response.json()

      // Show success message
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'E-way Bill generated successfully',
        life: 3000
      })

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result)
      }

      // Close modal and reset form
      setTimeout(() => {
        handleClose()
      }, 1000)
    } catch (error) {
      console.error('Error generating E-way Bill:', error)
      setApiError(error.message || 'Failed to generate E-way Bill')

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to generate E-way Bill',
        life: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Reset form when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      resetForm()
    }
  }, [isOpen])

  return (
    <>
      <Toast ref={toast} position='top-right' />

      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        size='lg'
        centered
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <ModalHeader toggle={handleClose}>
          <div className='d-flex align-items-center'>
            <i className='bi bi-truck me-2'></i>
            Generate E-way Bill
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Invoice Information Display */}
          {invoice && (
            <div className='alert alert-info mb-4'>
              <h6 className='mb-2'>
                <strong>Invoice Details</strong>
              </h6>
              <div className='row small'>
                <div className='col-md-6'>
                  <strong>Invoice No:</strong>{' '}
                  {invoice.invoiceNumber ||
                    invoice.proformaInvoiceNumber ||
                    '-'}
                </div>
                <div className='col-md-6'>
                  <strong>Total Value:</strong> ₹{' '}
                  {Number(invoice.totalInvoiceValue || 0).toLocaleString(
                    'en-IN',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                </div>
              </div>
            </div>
          )}

          {/* API Error Display */}
          {apiError && (
            <div className='alert alert-danger mb-3'>
              <i className='bi bi-exclamation-triangle me-2'></i>
              {apiError}
            </div>
          )}

          {/* Form Fields */}
          <div className='row'>
            {/* Transportation Mode */}
            <div className='col-md-6'>
              <FormGroup>
                <Label for='transMode'>
                  Transportation Mode <span className='text-danger'>*</span>
                </Label>
                <Input
                  type='select'
                  id='transMode'
                  value={formData.transMode}
                  onChange={e => handleInputChange('transMode', e.target.value)}
                  invalid={!!errors.transMode}
                  disabled={loading}
                >
                  {transportModeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {errors.transMode && (
                  <FormFeedback>{errors.transMode}</FormFeedback>
                )}
              </FormGroup>
            </div>

            {/* Vehicle Number - Only for Road Transport */}
            {formData.transMode === '1' && (
              <>
                <div className='col-md-6'>
                  <FormGroup>
                    <Label for='vehicleNo'>
                      Vehicle Number <span className='text-danger'>*</span>
                    </Label>
                    <Input
                      type='text'
                      id='vehicleNo'
                      placeholder='e.g., KA01AB1234'
                      value={formData.vehicleNo}
                      onChange={e =>
                        handleInputChange(
                          'vehicleNo',
                          e.target.value.toUpperCase()
                        )
                      }
                      invalid={!!errors.vehicleNo}
                      disabled={loading}
                      maxLength='15'
                    />
                    {errors.vehicleNo && (
                      <FormFeedback>{errors.vehicleNo}</FormFeedback>
                    )}
                  </FormGroup>
                </div>

                {/* Vehicle Type */}
                <div className='col-md-6'>
                  <FormGroup>
                    <Label for='vehicleType'>
                      Vehicle Type <span className='text-danger'>*</span>
                    </Label>
                    <Input
                      type='select'
                      id='vehicleType'
                      value={formData.vehicleType}
                      onChange={e =>
                        handleInputChange('vehicleType', e.target.value)
                      }
                      invalid={!!errors.vehicleType}
                      disabled={loading}
                    >
                      {vehicleTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    {errors.vehicleType && (
                      <FormFeedback>{errors.vehicleType}</FormFeedback>
                    )}
                  </FormGroup>
                </div>
              </>
            )}
            {/* Transporter Name */}
            <div className='col-md-6'>
              <FormGroup>
                <Label for='transporterName'>
                  Transporter Name{' '}
                  <span className='text-muted'>(Optional)</span>
                </Label>
                <Input
                  type='text'
                  id='transporterName'
                  placeholder='Enter transporter name'
                  value={formData.transporterName}
                  onChange={e =>
                    handleInputChange('transporterName', e.target.value)
                  }
                  invalid={!!errors.transporterName}
                  disabled={loading}
                  maxLength='100'
                />
                {errors.transporterName && (
                  <FormFeedback>{errors.transporterName}</FormFeedback>
                )}
              </FormGroup>
            </div>
          </div>

          {/* Information Note */}
          <div className='alert alert-light border mt-3 mb-0'>
            <small className='text-muted'>
              <i className='bi bi-info-circle me-2'></i>
              <strong>Note:</strong> E-way Bill is required for movement of
              goods worth more than ₹50,000. Ensure all details are accurate
              before submission.
            </small>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color='secondary' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button color='primary' onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Generating...
              </>
            ) : (
              <>
                <i className='bi bi-check-circle me-2'></i>
                Generate E-way Bill
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreateEwayBillModal
